import React from "react";
import Button from "shared/components/button";
import useAssignTicketService from "./service";
import { useCopies } from "shared/hooks/useCopies";
import DynamicFormRenderer from "shared/form/renderer";
import { AssignTicketCommonProps, AssignTicketFormProps, AssignTicketProps } from "./types";
import SearchNonAttendeeRegistration from "shared/components/searchNonAttendeeRegistrationForm";
import NoteBox from "../../../../components/noteBox";
import { useRegDetail } from "../../../../../../shared/context/regDetail";
import { useParticipantDetails } from "../../../../context/participantDetails";
import FieldViewType from "../../../../../../shared/enums/FieldViewType";
import { useQueryClient } from "react-query";
import { getBulkTicketDetailsKey } from "../../dataloader";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import BulkTicketNoteInPurchaseTicket from "../bulkTicketNoteInPurchaseTicket";
import { useAuth } from "shared/context/auth";
import { CanAssignTicketToSelf } from "shared/enums/TicketStatus";
import { useMyExperienceDashboardDetails } from "apps/myExperience/context/dashboard";
import { DashboardActionType } from "shared/enums/DashboardActionType";
import ButtonGroup from "shared/components/button/components/buttonGroup";

export function AssignTicketFromMyExperience(props: AssignTicketCommonProps) {
  const { name } = useRegDetail();
  const { id: registrationId, eventSeriesId, currencyCode } = useRegDetail();
  const { participantDetails } = useParticipantDetails();
  const { data: dashboardDataObj, refetch: refetchDashboard } = useMyExperienceDashboardDetails();
  const registrationParticipantId = participantDetails?.registrationParticipantId || "";
  const { userDetail } = useAuth();
  const queryClient = useQueryClient();
  const assignTicketSuccessHandler = React.useCallback(() => {
    props.handleClose();
    queryClient.refetchQueries(getBulkTicketDetailsKey(registrationParticipantId));
  }, [registrationParticipantId, props, queryClient]);

  const getValuesForSelfTransfer = React.useCallback(() => {
    return {
      email: userDetail?.email || "",
      nonAttendeeParticipant: { id: registrationParticipantId }
    };
  }, [registrationParticipantId, userDetail?.email]);

  const canAssignTicketToSelf = React.useMemo(() => {
    if (participantDetails?.ticketStatus === null) {
      return true;
    }
    if (participantDetails?.ticketStatus && dashboardDataObj) {
      return (
        !!dashboardDataObj?.registrationActions?.find(
          action => action.dashboardActionType === DashboardActionType.PURCHASE_TICKET
        ) && CanAssignTicketToSelf(participantDetails?.ticketStatus)
      );
    }
    return false;
  }, [dashboardDataObj, participantDetails?.ticketStatus]);

  const handleAssignTicketSuccess = React.useCallback(
    email => {
      const isSelfTransfer = email === userDetail?.email;
      if (isSelfTransfer) {
        refetchDashboard();
      }
    },
    [refetchDashboard, userDetail?.email]
  );

  return (
    <React.Fragment>
      <AssignTicket
        {...props}
        registrationId={registrationId}
        currencyCode={currencyCode}
        eventSeriesId={eventSeriesId}
        registrationParticipantId={registrationParticipantId}
        registrationName={name}
        assignTicketSuccessHandler={assignTicketSuccessHandler}
        getValuesForSelfTransfer={getValuesForSelfTransfer}
        canAssignTicketToSelf={canAssignTicketToSelf}
        handleAssignTicketSuccess={handleAssignTicketSuccess}
      />
    </React.Fragment>
  );
}

export default function AssignTicket(props: AssignTicketProps) {
  const copies = useCopies();
  const service = useAssignTicketService(props);

  return (
    <Modal className="assignTicketModal">
      <ModalHeader
        left={
          <span>
            {copies.get("assign.ticket")}
            <span className="assignTicketSubHeader">({props.ticketFormName})</span>
          </span>
        }
        onClose={props.handleClose}></ModalHeader>
      <ModalBody>
        <NoteBox>
          <p dangerouslySetInnerHTML={{ __html: copies.get("ticket.assign.note", { 0: props.registrationName }) }} />
        </NoteBox>

        <div className="assignTicketWrapper">
          {service.state.step === 0 && (
            <React.Fragment>
              {props.canAssignTicketToSelf && (
                <BulkTicketNoteInPurchaseTicket
                  bulkTicketId={props.bulkTicketId}
                  openAssignTicketToSelfHandler={() => {
                    props.getValuesForSelfTransfer &&
                      service.searchSuccessHandler(
                        props.getValuesForSelfTransfer().email,
                        props.getValuesForSelfTransfer().nonAttendeeParticipant
                      );
                  }}
                />
              )}
              <SearchNonAttendeeRegistration
                ticketFormId={props.ticketFormId}
                registrationId={props.registrationId}
                searchSuccessHandler={service.searchSuccessHandler}
                additionalLabel={copies.get("assign.ticket.to.who")}
                viewType={FieldViewType.TICKET_ASSIGNMENT}
              />
            </React.Fragment>
          )}
        </div>
        {service.state.step === 1 && service.assignTicketSchemaList.length > 0 && (
          <AssignTicketForm
            formKey={service.dataUpdatedAt.toString()}
            disabled={!!service.searchedNonAttendee?.id}
            schemaList={service.assignTicketSchemaList}
            submitHandler={service.submitHandler}
            assignFormSchema={service.assignFormSchema}
            eventSeriesId={props.eventSeriesId}
            currencyCode={props.currencyCode}
            registrationId={props.registrationId}
            registrationParticipantId={props.registrationParticipantId}
          />
        )}
      </ModalBody>
    </Modal>
  );
}

function AssignTicketForm(props: AssignTicketFormProps) {
  const copies = useCopies();
  const { eventSeriesId, currencyCode, registrationId, registrationParticipantId } = props;
  const formRef = React.useRef<any>();
  return (
    <React.Fragment>
      <DynamicFormRenderer
        key={props.formKey}
        disabled={props.disabled}
        enableReinitialize
        formRef={formRef}
        schemaList={props.schemaList}
        formSubmitHandler={props.submitHandler}
        parseValuesOnSubmitToApiReq={true}
        customData={{
          organizationId: props.assignFormSchema?.organizationId,
          registrationFormId: props.assignFormSchema?.registrationFormId,
          eventSeriesId: eventSeriesId,
          currencyCode,
          registrationId,
          registrationParticipantId: registrationParticipantId
        }}
      />

      <ButtonGroup inlineFormButtons className="assignTicketFooter">
        <Button
          label={copies.get("send.invite")}
          onClick={e => {
            e.preventDefault();
            formRef.current && formRef.current.submitForm();
          }}
        />
      </ButtonGroup>
    </React.Fragment>
  );
}
